import { useCallback, useEffect, useState } from 'react';
import { OAUTH_START_ORIGIN, apiFetch } from './api';
import { GOODBYE_PATH } from './goodbyeRoute';
import { clearJobsInboxCache } from './jobsInboxCache';
import { consumeSessionHandoff } from './sessionHandoff';

/** Mirrors apps/web/src/lib/useAuth.ts's SignedInUser shape — the same Lucia
 *  /auth/me contract, same-origin cookie session.
 *
 *  ADMIN-SWEEP (2026-07-06): `admin` is a NEW, optional, server-derived field
 *  (apps/api/routes/auth.routes.ts's GET /me — isAdminEmail() against the
 *  SAME ADMIN_EMAILS allowlist requireAdmin.ts enforces on every admin
 *  route). It is SERVE-ONLY: this app never infers admin-ness client-side,
 *  and this field grants nothing by itself — every real admin route stays
 *  independently gated server-side. Absent entirely on older cached shapes
 *  or a non-admin session; the FE treats absence exactly like `false`. */
export interface SignedInUser {
  id: string;
  email: string;
  displayName: string | null;
  picture: string | null;
  signedInAt: string | null;
  admin?: boolean;
}

interface State {
  user: SignedInUser | null;
  loading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<State>({ user: null, loading: true, error: null });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await apiFetch<{ user: SignedInUser | null }>('/auth/me');
      setState({ user: r.user, loading: false, error: null });
    } catch (err) {
      setState({
        user: null,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    // R188 session handoff: when the OAuth callback appended a one-time
    // ?session_handoff code (see sessionHandoff.ts), consume it FIRST — the
    // POST through the same-origin proxy sets the session cookie first-party
    // on this origin and strips the param — so the /auth/me probe below runs
    // with the cookie already in place. No param → immediate no-op.
    consumeSessionHandoff().finally(() => {
      void refresh();
    });
  }, [refresh]);

  const signIn = useCallback((returnTo = '/') => {
    const absoluteReturn = new URL(returnTo, window.location.origin).toString();
    // `origin` tells the callback which frontend to land back on (Studio vs
    // Throughline vs a local dev port) — the backend validates it against a
    // strict allowlist and signs it into the OAuth state; an unlisted origin
    // falls back to APP_BASE_URL server-side. See apps/api/routes/auth.routes.ts.
    const params = new URLSearchParams({
      return_to: absoluteReturn,
      origin: window.location.origin,
    });
    // OAUTH_START_ORIGIN, not API_ORIGIN: the start leg must be a top-level
    // navigation to the WORKER origin so the state cookie lands where the
    // callback (Google's registered redirect_uri) can read it — the one
    // deliberate exception to this app's same-origin-proxy rule. See the
    // constant's own doc comment in api.ts.
    window.location.href = `${OAUTH_START_ORIGIN}/auth/google?${params.toString()}`;
  }, []);

  const signOut = useCallback(async () => {
    await requestSignOut();
    setState(SIGNED_OUT_STATE);
    // W14 — the jobs-inbox cache (jobsInboxCache.ts) has a sessionStorage
    // tier that a hard reload does NOT clear (unlike the in-memory tier —
    // see R207's own reasoning just below). Without this, a later sign-in on
    // the SAME tab could hydrate useJobsInbox's very first render from a
    // PRIOR account's cached job list. Explicit, not incidental.
    clearJobsInboxCache();
    // R207: never strand a signed-out user on a dead authed screen — land on
    // the goodbye page via a HARD navigation (full reload = every hook's
    // in-memory state cleanly gone, the same reasoning as the OAuth start leg).
    window.location.assign(GOODBYE_PATH);
  }, []);

  return { ...state, refresh, signIn, signOut };
}

/** #49 — the actual POST /auth/logout call, pulled out of the signOut
 *  callback body (additive, zero behavior change) so useAuth.signOut.test.ts
 *  can prove the exact request THIS hook's Sign out button triggers, the
 *  same "extract the testable core" idiom onboardingStep.ts/ThemeProvider's
 *  readStoredTheme already establish for a hook/component this repo's
 *  jsdom-free test setup cannot render directly. */
export async function requestSignOut(): Promise<void> {
  await apiFetch('/auth/logout', { method: 'POST' });
}

/** The exact state signOut resolves to on success — named + exported so a
 *  test can assert against the SAME literal the hook itself sets, rather
 *  than a hand-typed duplicate that could silently drift from it. */
export const SIGNED_OUT_STATE: State = { user: null, loading: false, error: null };
